import type { ReactNode } from 'react';

import { normalizeFieldHelpProps } from './normalizeFieldHelpProps';

export type VNFieldHelpProps = {
  /** Hover tooltip on the info icon */
  helperTooltip?: string;
  /** Inline explanation shown on click (light-blue panel below field) */
  helperText?: string;
  /** Rich HTML/React fragment; opens right slideout drawer on click */
  helperContent?: ReactNode;
  /** External link rendered below helperText and/or inside drawer */
  helperLink?: string;
  /** Optional drawer title when helperContent is set (defaults to "Help") */
  helperContentTitle?: string;
  /** Per-option hover tooltips in open menus (key = option value/id) */
  dropDownItemHelperTooltip?: Record<string, string>;
  /** Per-option persistent subtext in open menus (key = option value/id) */
  dropDownItemHelperText?: Record<string, string>;

  /** @deprecated Use `helperTooltip` instead */
  infoTooltip?: ReactNode;
  /** @deprecated Use `helperText` instead */
  helpText?: ReactNode;
  /** @deprecated Use `helperLink` instead */
  learnMore?: string | { href: string; label?: string };
  /** @deprecated Use `helperContent` instead */
  drawerContent?: ReactNode;
  /** @deprecated Use `helperContentTitle` instead */
  drawerTitle?: string;
  /** @deprecated Use `dropDownItemHelperTooltip` instead */
  dropDownItemToolTips?: Record<string, string>;
};

export type NormalizedFieldHelpProps = {
  helperTooltip?: string;
  helperText?: string;
  helperContent?: ReactNode;
  helperLink?: string;
  helperContentTitle?: string;
  dropDownItemHelperTooltip?: Record<string, string>;
  dropDownItemHelperText?: Record<string, string>;
};

export function hasFieldHelp(props: VNFieldHelpProps): boolean {
  const normalized = normalizeFieldHelpProps(props);
  return Boolean(
    normalized.helperTooltip ||
      normalized.helperText ||
      normalized.helperLink ||
      normalized.helperContent ||
      (normalized.dropDownItemHelperTooltip && Object.keys(normalized.dropDownItemHelperTooltip).length > 0) ||
      (normalized.dropDownItemHelperText && Object.keys(normalized.dropDownItemHelperText).length > 0)
  );
}

export function hasFieldHelpIcon(props: VNFieldHelpProps): boolean {
  const { helperTooltip, helperText, helperContent, helperLink } = normalizeFieldHelpProps(props);
  return Boolean(helperTooltip || helperText || helperContent || helperLink);
}

export function hasClickableFieldHelp(props: VNFieldHelpProps): boolean {
  const { helperText, helperLink, helperContent } = normalizeFieldHelpProps(props);
  return Boolean(helperText || helperLink || helperContent);
}
